import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Lock, Loader2, Zap } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/stores/authStore'
import { useConfig } from '@/hooks/useConfig'
import { APP_BASE_PATH } from '@/lib/constants'
import {
  buildOAuthStartUrl,
  markOAuthAutoStartAttempted,
  shouldAutoStartOAuth,
} from '@/lib/oauthAutoStart'

/**
 * 웍스 인증 필요 페이지
 *
 * 인증되지 않은 사용자가 메인 페이지(/)에 접근할 때 표시한다.
 * - OAuth 로그인이 켜져 있으면 "웍스로 인증하기" 버튼은 백엔드의 /api/auth/oauth/start 로 간다
 *   (Authorization Code + PKCE, 완료 후 /auth/callback 으로 복귀). 포탈에서 앱 URL 로 바로
 *   진입한 경우를 위해 브라우저 세션당 1회 prompt=none 으로 자동 시도한다.
 * - 꺼져 있으면 종전처럼 배포 도메인(APP_SOURCE)에 맞는 웍스 포탈(loginUrl)로 이동한다.
 * 개발 환경에서는 FREE PASS 만 추가로 표시.
 */
export function AuthRequired() {
  const { t } = useTranslation()
  const { loginWithFreepass } = useAuthStore()
  const [freepassLoading, setFreepassLoading] = useState(false)
  const { pathname, search } = useLocation()

  const { data: config, isLoading } = useConfig()
  const [autoStarting, setAutoStarting] = useState(false)

  const returnTo = `${pathname}${search}`
  const oauthEnabled = config?.oauthLoginEnabled === true

  useEffect(() => {
    if (!config || !shouldAutoStartOAuth(pathname, config)) return
    markOAuthAutoStartAttempted()
    // 바로 페이지를 떠나므로 이 상태는 언로드까지 스피너를 유지하는 용도뿐이다
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAutoStarting(true)
    window.location.assign(buildOAuthStartUrl(config, { prompt: 'none', returnTo }))
  }, [config, pathname, returnTo])

  const handleFreepass = async () => {
    setFreepassLoading(true)
    try {
      await loginWithFreepass()
      window.location.href = APP_BASE_PATH
    } catch (err) {
      console.error('FREE PASS failed:', err)
      setFreepassLoading(false)
    }
  }

  const loginHref = oauthEnabled
    ? buildOAuthStartUrl(config, { returnTo })
    : (config?.loginUrl ?? 'https://wrks.ai/')

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4">
      <div className="absolute right-4 top-4">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-sm text-center">
        {/* Icon */}
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-slate-100">
          <Lock className="h-10 w-10 text-slate-600" />
        </div>

        {/* Title */}
        <h1 className="mb-2 text-3xl font-bold text-slate-900">{t('pages.authTitle')}</h1>

        {/* Description */}
        <p className="mb-8 text-slate-600">{t('pages.authDescription1')}</p>

        {/* Wrks login — OAuth start (preferred) or the legacy portal */}
        {isLoading || autoStarting ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <Button asChild variant="brand" className="h-auto w-full rounded-lg py-3.5 text-base">
            <a href={loginHref}>{t('pages.authLoginButton')}</a>
          </Button>
        )}

        {/* FREE PASS (Development Only) */}
        {config?.freepassEnabled && (
          <div className="mt-8 border-t border-slate-200 pt-6">
            <button
              onClick={handleFreepass}
              disabled={freepassLoading}
              className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 p-4 text-amber-700 transition hover:border-amber-400 hover:bg-amber-100 disabled:opacity-50"
            >
              {freepassLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Zap className="h-5 w-5" />
              )}
              <span className="font-medium">{t('pages.authFreepass')}</span>
            </button>
            <p className="mt-2 text-center text-xs text-slate-500">
              {t('pages.authFreepassDescription')}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
